import { Outlet, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'

import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { Footer } from './Footer'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { cn } from '@/lib/utils/cn'

/**
 * Application frame.
 *
 * Rail, bar, content — the shape of a messaging client, and conventional on purpose.
 * Inventing novel navigation for an operations tool costs the user something and gains
 * nothing; the distinctive work belongs in how the data is presented.
 *
 * The rail is one target wide, so it fits a phone as it is. That removes the drawer this
 * shell used to open, and with it the scrim, the toggle, and the state that said whether
 * the navigation was showing: on every screen, it is.
 */
export function AppShell() {
  return (
    <RequireAuth>
      <AppFrame />
    </RequireAuth>
  )
}

function AppFrame() {
  const [menuOpen, setMenuOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  
  // Hide footer on chat to preserve its full-height workspace layout
  const isChat = pathname === '/chat' || pathname.startsWith('/chat/')

  return (
    <div className="flex h-full bg-ground">
      <aside className="hidden z-20 shrink-0 border-r border-line bg-surface md:block">
        <Sidebar />
      </aside>

      <>
        <button
          type="button"
          aria-label="Close menu"
          className={cn(
            'fixed inset-0 z-40 cursor-default bg-black/60 backdrop-blur-[1px] transition-opacity duration-200 md:hidden',
            menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={() => setMenuOpen(false)}
        />
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex flex-col bg-ground transition-transform duration-200 ease-in-out md:hidden rounded-r-2xl overflow-hidden shadow-2xl',
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <Sidebar onNavigate={() => setMenuOpen(false)} isMobile={true} />
        </aside>
      </>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenuClick={() => setMenuOpen(true)} />
        <main className="min-h-0 flex-1 overflow-y-auto flex flex-col">
          <div className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
          {!isChat && <Footer />}
        </main>
      </div>
    </div>
  )
}
